import { gql } from '@apollo/client';
import { SortBy } from 'spw-components/collab/types';
import {
  ClientForm,
  ClientSlimForDetail,
  ClientSlimForList,
  IndustrySlimForList,
} from 'spw-redux/modules/clients/types';
import {
  ProjectsSortingParams,
  ProjectSlimForList,
} from 'spw-redux/modules/projects/types';
import { graphqlClient } from './utils';

export const getIndustries = () =>
  graphqlClient.query<{ industries: IndustrySlimForList[] }>({
    fetchPolicy: 'no-cache',
    query: gql`
      query GetIndustries {
        industries: getIndustries {
          id
          name
        }
      }
    `,
  });

export const getClients = (
  page: number,
  perPage: number,
  sort: SortBy,
  industryId?: number,
  name?: string
) =>
  graphqlClient.query<{ clients: { clients: ClientSlimForList[] } }>({
    fetchPolicy: 'no-cache',
    query: gql`
      query GetClients($page: Int!, $perPage: Int!, $industryId: Int, $name: String) {
          clients: getClients(page: $page, perPage: $perPage, industryId: $industryId, name: $name, sort: {${
            sort.column
          }: ${sort.direction.toUpperCase()}}) {
              clients {
                  id
                  name
                  industry {
                      id
                      name
                  }
                  projects {
                      id
                      archived
                  }
                  numberOfTeams
              }
          }
      }
  `,
    variables: {
      page,
      perPage,
      industryId,
      name,
    },
  });

export const getClient = (clientId: number) =>
  graphqlClient.query<{ client: ClientSlimForDetail }>({
    fetchPolicy: 'no-cache',
    query: gql`
      query GetClient($clientId: Int!) {
        client: getClient(id: $clientId) {
          id
          name
          hasLogo
          thumbnailS3Path
          industry {
            id
            name
          }
          numberOfTeams
        }
      }
    `,
    variables: {
      clientId,
    },
  });

export const createClient = (clientForm: ClientForm) =>
  graphqlClient.mutate<{ client: ClientSlimForList }>({
    mutation: gql`
      mutation CreateClient($name: String!, $industryId: Int!) {
        client: createClient(name: $name, industryId: $industryId) {
          id
          name
          industry {
            id
            name
          }
          organization {
            id
            numberOfProjects
          }
        }
      }
    `,
    variables: {
      industryId: clientForm.selectedIndustry.id,
      name: clientForm.name,
    },
  });

export const deleteClient = (clientId: number) =>
  graphqlClient.mutate<{ client: { id: number } }>({
    mutation: gql`
      mutation DeleteClient($clientId: Int!) {
        client: deleteClient(id: $clientId) {
          id
        }
      }
    `,
    variables: {
      clientId,
    },
  });

export const updateClient = (
  id: number,
  image?: string,
  industryId?: number,
  name?: string
) =>
  graphqlClient.mutate<{ client: ClientSlimForDetail }>({
    mutation: gql`
      mutation UpdateClient(
        $id: Int!
        $name: String
        $image: String
        $industryId: Int
      ) {
        client: updateClient(
          id: $id
          image: $image
          industryId: $industryId
          name: $name
        ) {
          id
          name
          hasLogo
          thumbnailS3Path
          industry {
            id
            name
          }
          numberOfTeams
        }
      }
    `,
    variables: {
      id,
      image,
      industryId,
      name,
    },
  });

export const getProjectsForClient = (
  clientId: number,
  sort: ProjectsSortingParams,
  page: number,
  itemsPerPage: number
) =>
  graphqlClient.query<{
    projects: { projects: ProjectSlimForList[]; total: number };
  }>({
    fetchPolicy: 'no-cache',
    query: gql`
      query GetProjectsForClient(
        $clientId: Int
        $sort: ProjectSortOptions
        $page: Int
        $itemsPerPage: Int
      ) {
        projects: projects(
          clientId: $clientId
          sort: $sort
          page: $page
          perPage: $itemsPerPage
        ) {
          total
          projects {
            id
            projectName
            projectNumber
            projectOwner {
              id
              name
            }
            lastEditor {
              id
              name
              isSuperAdmin
            }
            teams {
              id
              name
              hasLogo
              thumbnailS3Path
            }
            buildings {
              id
              index
              city
              state
              countryCode
              streetAddress
            }
            type
            updatedAt
            archived
            status
            requiresRequestToJoin
            hasMissingBenchmarkData
          }
        }
      }
    `,
    variables: { clientId, sort, page, itemsPerPage },
  });

export const searchClientsByName = (params: { name: string }) =>
  graphqlClient.query<{ clients: { clients: ClientSlimForList[] } }>({
    fetchPolicy: 'no-cache',
    query: gql`
      query SearchClientsByName($industryId: Int, $name: String) {
        clients: getClients(
          perPage: 9999
          industryId: $industryId
          name: $name
          sort: { name: ASC }
        ) {
          clients {
            id
            name
            industry {
              id
              name
            }
          }
        }
      }
    `,
    variables: params,
  });

export const checkClientExistenceByName = (params: { name: string }) =>
  graphqlClient.query<{ clientExists: boolean }>({
    fetchPolicy: 'no-cache',
    query: gql`
      query CheckClientExistenceByName($name: String!) {
        clientExists: doesClientWithParamsExist(name: $name)
      }
    `,
    variables: params,
  });
